import { useCallback, useEffect } from 'react';
import { ArticleContainer } from 'src/components/Article';
import { AsidePanelContainer } from 'src/components/AsidePanel/AsidePanelContainer';
import { useAsidePanelContext } from 'src/components/AsidePanel/contexts/AsidePanelContext';
import Container from 'src/components/Container/Container';
import { usePromptPageContext } from 'src/components/Prompt/context/PromptPageContext';
import { useProofOfAttentionTracker } from 'src/services/poa-tracker/useProofOfAttentionTracker';
import { PromptReplyPreviewNavbar } from './PromptReplyPreviewNavbar';
import { AnimatedPage } from '../AnimatedPage/AnimatedPage';
import { usePromptReplyPreviewNavigation } from './hooks/usePromptReplyPreviewNavigation';
import { useAnimatedPage } from '../AnimatedPage/hooks/useAnimatedPage';

const ANIMATION_DURATION = 500; // ms;

export const PromptReplyPreview = () => {
  const { animate, direction, isInProgress: isAnimationInProgress } = useAnimatedPage(ANIMATION_DURATION);
  const { preview } = usePromptPageContext();
  const { setOnClose } = useAsidePanelContext();
  const { openAsidePanel, closeAsidePanel } = useAsidePanelContext();
  const { next, prev, prevId, nextId, hasMore } = usePromptReplyPreviewNavigation();

  const resetPreview = useCallback(() => {
    preview.setState({ articleId: null });
  }, []);

  const onPrevClick = useCallback(animate(prev, { direction: 'backwards' }), [prev]);

  const onNextClick = useCallback(animate(next, { direction: 'forwards' }), [next]);

  useProofOfAttentionTracker(preview.state.articleId);

  useEffect(() => {
    setOnClose?.(resetPreview);
  }, [resetPreview, setOnClose]);

  useEffect(() => {
    if (preview.state.articleId) {
      openAsidePanel();
    } else {
      closeAsidePanel();
    }
  }, [preview.state.articleId]);

  return (
    <AsidePanelContainer placement="bottom" rounded="md" containerClassName="!w-full sm:!w-3/4 max-w-[1440px]">
      <div className="flex flex-col items-center overflow-x-hidden">
        <PromptReplyPreviewNavbar
          prevButton={{
            onClick: onPrevClick,
            disabled: !prevId,
            loading: isAnimationInProgress,
          }}
          nextButton={{
            onClick: onNextClick,
            disabled: !nextId && !hasMore,
            loading: isAnimationInProgress,
          }}
        />
        <div className="pt-10" />
        {preview.state.articleId && (
          <AnimatedPage duration={ANIMATION_DURATION} direction={direction}>
            <Container isInsideContainer className="relative md:grid-cols-12 md:gap-x-3 !max-w-full">
              <ArticleContainer
                forceLoading={isAnimationInProgress}
                shouldEnableEdit={false}
                articleId={preview.state.articleId}
              />
            </Container>
          </AnimatedPage>
        )}
      </div>
    </AsidePanelContainer>
  );
};
